<!-- Copyright 2020 Adobe. All rights reserved.
This file is licensed to you under the Apache License, Version 2.0 (the "License");
you may not use this file except in compliance with the License. You may obtain a copy
of the License at http://www.apache.org/licenses/LICENSE-2.0
Unless required by applicable law or agreed to in writing, software distributed under
the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
OF ANY KIND, either express or implied. See the License for the specific language
governing permissions and limitations under the License. -->

import {Layout} from '@react-spectrum/docs';
export default Layout;

import docs from 'docs:@react-spectrum/buttongroup';
import {HeaderInfo, PropTable} from '@react-spectrum/docs';
import packageData from '../package.json';

```jsx import
import {Button} from '@react-spectrum/button';
import {ButtonGroup} from '@react-spectrum/buttongroup';
import {View} from '@react-spectrum/view';
```

---
category: Buttons
---

# ButtonGroup

<p>{docs.exports.ButtonGroup.description}</p>

<HeaderInfo
  packageData={packageData}
  componentNames={['ButtonGroup']}
  sourceData={[
    {type: 'Spectrum', url: 'https://spectrum.adobe.com/page/button/#Button-group-overflow'}
  ]} />

## Example
To see the orientation switch, reduce the width of your window.
```tsx example
<ButtonGroup width="50%">
  <Button variant="primary">Rate Now</Button>
  <Button variant="secondary">No, thanks</Button>
  <Button variant="secondary">Remind me later</Button>
</ButtonGroup>
```

## Content

ButtonGroup expects [Buttons](./Button.html) as children.

## Props

<PropTable component={docs.exports.ButtonGroup} links={docs.links} />

## Visual options

### Orientation

Setting this to 'vertical' will prevent any dynamic orientation changes, the ButtonGroup will remain in vertical regardless of the available width.
```tsx example
<ButtonGroup orientation="vertical">
  <Button variant="secondary">No, thanks</Button>
  <Button variant="secondary">Remind me later</Button>
  <Button variant="primary">Rate Now</Button>
</ButtonGroup>
```

### Alignment

Button groups are aligned contextually. Generally (in LTR languages), button groups should be left-aligned to follow content. However, they should be right-aligned inside container components, such as in dialogs, popovers, or cards. They should be center-aligned in the context of an empty state. In RTL, left and right should be flipped.
To see the orientation switch, reduce the width of your window.
```tsx example
<View width="50%">
  <ButtonGroup align="end" width="100%">
    <Button variant="secondary">No, thanks</Button>
    <Button variant="secondary">Remind me later</Button>
    <Button variant="primary">Rate Now</Button>
  </ButtonGroup>
</View>
```

```tsx example
<View width="50%">
  <ButtonGroup align="center" width="100%">
    <Button variant="secondary">No, thanks</Button>
    <Button variant="secondary">Remind me later</Button>
    <Button variant="primary">Rate Now</Button>
  </ButtonGroup>
</View>
```
